<template>
    <ValidationObserver
        ref="connectArtistToReleaseDialog"
        v-slot="{ passes }"
    >
        <BaseDialog
            :showDialog="showDialog"
            :headline="headline"
            :close="close"
            maxDialogWidth="800"
            maxCardHeight="800"
            :alvqa="alvqa"
        >
            <template v-slot:dialogText>
                <v-container fluid v-if="!showAzFields">
                    <!-- Search -->
                    <v-row>
                        <v-col cols="10">
                            <SearchFilter 
                                :context="context" 
                                @keydown.enter="searchForListItems"
                                :alvqa="alvqa + '-textfield-search'"
                            />
                        </v-col>
                        <v-col align-self="end" class="text-left">
                            <BaseIcon 
                                @click="searchForListItems" 
                                icon="fa-search" 
                                tooltipText="lang_GENERIC_CAPTION_SEARCH"
                                :alvqa="alvqa + '-button-search'"
                            />
                        </v-col>
                    </v-row>
                    <!-- Results -->
                    <v-row v-if="showSearchResult" class="pb-3">
                        <v-col v-if="items.length !== 0">
                            <BaseHeaderStats 
                                :context="context" 
                                typeStats="DIALOG"
                                :alvqa="alvqa"
                                useContextForTotal="ConnectArtistToReleasesDialog"
                            />
                        </v-col>
                        <v-col 
                            v-else
                            :alvqa="alvqa + '-text-none-found'"
                        >
                        0 {{ $t('lang_GENERAL_CAPTION_FOUND') }}
                        </v-col>
                    </v-row>
                    <v-divider v-if="items.length > 0"></v-divider>
                    <v-list two-line>
                        <v-list-item-group
                            v-model="selected"
                            :multiple="false"
                        >
                            <v-list-item
                                v-for="item in items"
                                :key="item.alvId"
                                :value="item"
                                :alvqa="alvqa + '-result-list-row-' + item.alvId"
                                @click="selectItem(item)"
                            >
                                <v-list-item-content>
                                    <v-list-item-title :alvqa="alvqa + '-result-list-row-' + item.alvId + '-name'">
                                        <!-- {{ item.gemaName }}
                                        {{ $alvPlugin.printName(item) ? '(' + item.firstName + ' ' + item.lastName +')' : ''}} -->
                                        {{ $alvPlugin.showNameOfAzEntry(item) }}
                                        <v-avatar color="grey" size="20" class="caption">{{ item.typeOfAz }}</v-avatar>
                                    </v-list-item-title>
                                    <v-list-item-subtitle :alvqa="alvqa + '-result-list-row-' + item.alvId + '-subtitle'">
                                        {{ $t('lang_IPI') }}: {{ item.ipi !== '' ? item.ipi : $t('lang_GENERIC_CAPTION_NONE') }} | {{ item.alvId }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list-item-group>
                    </v-list>
                </v-container>

                <!-- Fields for new Artist -->
                <v-container fluid v-else-if="showAzFields">
                    <v-row>
                        <v-col cols="6">
                            <BaseEditPageDropdown 
                                context="LabelReleases"
                                field="SelectedAzTypeForArtist"
                                :items="azTypes" 
                                :label="$t('lang_GENERIC_CAPTION_AZ_TYPE')"  
                                rules="required"
                                :withMargin="false"
                                :clearable="false"
                                :alvqa="alvqa + '-dropdown-az-type'"
                                :additionalFunction="changeAzType"
                            />
                        </v-col>
                    </v-row>
                    <!-- FOR GROUPS -->
                    <v-row v-if="selectedAzType === 'groups'">
                        <v-col>
                            <BaseEditPageTextField 
                                :context="subcontext"
                                field="Group"
                                :label="$t('lang_AZ_GROUPS_CAPTION_GROUP_NAME')" 
                                rules="required"
                                :withMargin="false"
                                :alvqa="alvqa + '-textfield-group-name'"
                            />
                        </v-col>
                        <v-col>
                            <BaseEditPageDropdown 
                                :context="subcontext"
                                field="Country"
                                :items="countries" 
                                :label="$t('lang_AZ_PERSONS_CAPTION_COUNTRY')"  
                                rules="required"
                                :withMargin="false"
                                :alvqa="alvqa + '-dropdown-country'"
                            />
                        </v-col>
                    </v-row>
                    <!-- FOR PERSONS -->
                    <v-row v-if="selectedAzType === 'persons'">
                        <v-col>
                            <BaseEditPageTextField 
                                :context="subcontext"
                                field="Group"
                                :label="$t('lang_AZ_PERSONS_CAPTION_COMPANY_GROUP_ARTIST_NAME')" 
                                :rules="isGroupRequired"
                                :alvqa="alvqa + '-textfield-group'"
                                :withMargin="false"
                            />
                        </v-col>
                        <v-col>
                            <BaseEditPageDropdown 
                                :context="subcontext"
                                field="Country"
                                :items="countries" 
                                :label="$t('lang_AZ_PERSONS_CAPTION_COUNTRY')"  
                                rules="required"
                                :withMargin="false"
                                :alvqa="alvqa + '-dropdown-country'"
                            />
                        </v-col>
                    </v-row>
                    <v-row v-if="selectedAzType === 'persons'">
                        <v-col>
                            <BaseEditPageTextField 
                                :context="subcontext"
                                field="FirstName"
                                :label="$t('lang_AZ_PERSONS_CAPTION_FIRST_NAME')"  
                                :rules="areNamesRequiered"
                                :withMargin="false"
                                :alvqa="alvqa + '-textfield-first-name'"
                            />
                        </v-col>
                        <v-col>
                            <BaseEditPageTextField 
                                :context="subcontext"
                                field="LastName"
                                :label="$t('lang_AZ_PERSONS_CAPTION_LAST_NAME')"   
                                :rules="areNamesRequiered"
                                :withMargin="false"
                                :alvqa="alvqa + '-textfield-last-name'"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </template>

            <!--  Buttons -->
            <template v-slot:dialogActions>
                <v-container fluid>
                    <v-row v-if="!showAzFields">
                        <!-- <v-spacer /> -->
                        <v-col v-if="selectedArtist.length !== 0">
                            <BaseCheckbox 
                                :context="'LabelReleases' + context + 'ConnectToAllTracks'"
                                :label="$t('lang_LABEL_RELEASES_CAPTION_ADD_TO_ALL_CONNECTED_TRACKS')" 
                                valueOuter="connectToAllTracks"
                                hideDetails
                                class="mt-0"
                                :alvqa="alvqa + '-checkbox-add-to-connected-tracks'"
                            />
                        </v-col>
                        <v-col class="text-right">
                            <BaseButton 
                                :disabled="selectedArtist.length !== 0" 
                                @click="createAz()"
                                label="lang_GENERIC_CREATE_NEW_AZ_ENTRY"
                                :alvqa="alvqa +  '-button-create'"
                            />
                        </v-col>
                        <v-col cols="3" class="text-right">
                            <BaseButton 
                                :disabled="selectedArtist.length === 0" 
                                @click="connectPerformer()"
                                label="lang_GENERIC_CAPTION_CONNECT"
                                :alvqa="alvqa +  '-button-select'"
                            />
                        </v-col>
                    </v-row>
                    <v-row v-else>
                        <!-- <v-spacer /> -->
                        <v-col cols="5" v-if="selectedAzType !== null">
                            <BaseCheckbox 
                                :context="'LabelReleases' + context + 'ConnectToAllTracks'"
                                :label="$t('lang_LABEL_RELEASES_CAPTION_ADD_TO_ALL_CONNECTED_TRACKS')" 
                                valueOuter="connectToAllTracks"
                                hideDetails
                                class="mt-0"
                                :alvqa="alvqa + '-checkbox-add-to-connected-tracks'"
                            />
                        </v-col>
                        <v-col class="text-right">
                            <BaseButton 
                                @click="backToSearch()"
                                label="lang_GENERAL_BUTTON_CANCEL"
                                :alvqa="alvqa +  '-button-back'"
                            />
                        </v-col>
                        <v-col class="text-right">
                            <BaseButton 
                                @click="passes(connectAndSavePerformer)"
                                :disabled="selectedAzType === null"
                                label="lang_GENERIC_CAPTION_SAVE_AND_CONNECT"
                                :alvqa="alvqa +  '-button-save-connect'"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </template>
        </BaseDialog>
    </ValidationObserver>
</template>

<script>
import BaseDialog from '@/components/_base/BaseDialog'
import BaseButton from '@/components/_buttons/BaseButton'
import SearchFilter from '@/components/_filters/SearchFilter'
import BaseIcon from '@/components/_buttons/BaseIcon'
import BaseHeaderStats from '@/components/_base/BaseHeaderStats'
import BaseEditPageDropdown from '@/components/_editForm/BaseEditPageDropdown'
import BaseEditPageTextField from '@/components/_editForm/BaseEditPageTextField'
import BaseCheckbox from '@/components/_base/BaseCheckbox'

export default {
    name: "ConnectArtistToRelease",
    components: {
        BaseDialog,
        BaseButton,
        BaseIcon,
        SearchFilter,
        BaseHeaderStats,
        BaseEditPageDropdown,
        BaseEditPageTextField,
        BaseCheckbox
    },
    data() {
        return {
            headline: 'lang_LABEL_TRACKS_CAPTION_CONNECT_INTERPRET',
            alvqa: "label-releases-edit-form",
            showAzFields: false,
            context: 'ArtistForReleases',
            selected: [],
            items: [],
            showSearchResult: false,
        }
    },
    computed: {
        showDialog: {
            get()       { return this.$store.getters.getShowConnectArtistDialog },
            set(show)   { this.$store.commit('setShowConnectArtistDialog', show) }
        },
        selectedArtist: {
            get()       { return this.$store.getters.getSelectedArtistForReleases },
            set(artist) { this.$store.commit('setSelectedArtistForReleases', artist) }
        },
        connectToAllTracksCheckbox: {
            get()       { return this.$store.getters.getLabelReleasesArtistForReleasesConnectToAllTracksCheckbox },
            set(value) { this.$store.commit('setLabelReleasesArtistForReleasesConnectToAllTracksCheckbox', value) }
        },
        azTypes() {
            return [
                {value: "groups", text: this.$t('lang_AZ_GROUPS')},
                {value: "persons", text: this.$t('lang_AZ_PERSONS')},
            ]
        },
        selectedAzType: {
            get() { return this.$store.getters.getLabelReleasesDetailsSelectedAzTypeForArtist },
            set(type) { this.$store.commit('setLabelReleasesDetailsSelectedAzTypeForArtist', type) }
        },
        // Validation of PERSONS fields
        isGroupRequired() {
            let personDetails = this.$store.getters.getAzPersonsDetails
            if(personDetails === undefined) return 'required'
            else {
                let isFirstnameLastNameNull = personDetails.firstName === null || personDetails.lastName === null
                if(isFirstnameLastNameNull) return 'required'
                else return ''
            }
        },
        areNamesRequiered() {
            let personDetails = this.$store.getters.getAzPersonsDetails
            if(personDetails === undefined) return 'required'
            else { 
                if(personDetails.group === null) return 'required'
                else return ''
            }
        },
        subcontext() { return 'Az' + this.selectedAzType.charAt(0).toUpperCase() + this.selectedAzType.slice(1) },
        countries() { return this.$alvPlugin.getListOfTranslatedCountries() },
    },
    methods: {
        close() {
            this.showDialog = false
            this.headline = 'lang_LABEL_TRACKS_CAPTION_CONNECT_INTERPRET'
            this.showAzFields = false
            this.selectedArtist = []
            this.selected = []
            this.items = []
            this.showSearchResult = false
            // this.connectToAllTracksCheckbox = []
            this.$store.commit('setArtistForReleasesSearch', '')
            this.$store.commit('setLabelReleasesDetailsSelectedAzTypeForArtist', null)
            this.$store.commit('setAzPersonsDetails', this.$store.getters.getDefaultNewAzPersons)
            this.$store.commit('setAzGroupsDetails', this.$store.getters.getDefaultNewAzGroups)
        },
        async searchForListItems() {
            this.$store.commit('setIsLoading', true)
            let search = this.$store.getters.getArtistForReleasesSearch
            if(search) {
                this.selectedArtist = []
                this.selected = []
                this.showSearchResult = true
                this.items = await this.$store.dispatch('searchArtistForReleases')
            }

            if(!search) this.items = []
            this.$store.commit('setIsLoading', false)
        },
        selectItem(item) {
            if(this.selectedArtist.length === 0) {
                this.selectedArtist.push(item)
            } else {
                if(this.selectedArtist[0]["id"] === item.id) {
                    this.selectedArtist = []
                }
                else {
                    this.selectedArtist = []
                    this.selectedArtist.push(item)
                }
            }
        },
        changeAzType(selected) {
            if(selected) {
                let context = this.selectedAzType.charAt(0).toUpperCase() + this.selectedAzType.slice(1)
                let defaultAz = JSON.parse(JSON.stringify(this.$store.getters['getDefaultNewAz' + context]))
                this.$store.commit('setAz' + context + 'Details', defaultAz)
                this.$refs.connectArtistToReleaseDialog.reset()
            }
        },
        createAz() {
            this.showAzFields = true
            this.headline = "lang_GENERIC_CREATE_NEW_AZ_ENTRY"
        },
        connectPerformer() {
            this.$store.dispatch('addArtistToReleases')
            this.close()
        },
        backToSearch() {
            this.showAzFields = false
            this.$store.commit('setLabelReleasesDetailsSelectedAzTypeForArtist', null)
            
            let defaultPerson = JSON.parse(JSON.stringify(this.$store.getters.getDefaultNewAzPersons))
            let defaultGroup = JSON.parse(JSON.stringify(this.$store.getters.getDefaultNewAzGroups))
            this.$store.commit('setAzPersonsDetails', defaultPerson)
            this.$store.commit('setAzGroupsDetails', defaultGroup)
        },
        async connectAndSavePerformer() {
            let context = this.selectedAzType.charAt(0).toUpperCase() + this.selectedAzType.slice(1)
            
            // Get data from Dialog
            let newArtist = this.$store.getters['getAz' + context + 'Details']
            // Set type to artist
            newArtist['type'] = ["artist"]
            // Save the new Person/Group
            await this.$store.dispatch('saveAz' + context + 'Form', false)
            // Get the saved new artist from BE
            let artist = this.$store.getters['getAz' + context + 'Details']
            // Set the saved artist to the store for the next step (connect artist to release)
            this.$store.commit('setSelectedArtistForReleases', [artist])
            // connect artist/performer to release
            this.connectPerformer()
        },
    },
    mounted() {
        this.$store.commit('setCountries')
    }
}
</script>